import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { heroImages } from "../data/cases";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";
import { seoByPath } from "../lib/seo";
import Img from "../components/Img";

const Arkitekter = () => {
    const { t } = useLang();

    const caps = [
        { title: t.arkitekter.c1, text: t.arkitekter.c1Text },
        { title: t.arkitekter.c2, text: t.arkitekter.c2Text },
        { title: t.arkitekter.c3, text: t.arkitekter.c3Text },
        { title: t.arkitekter.c4, text: t.arkitekter.c4Text },
    ];

    return (
        <main data-testid="page-arkitekter" className="bg-white text-[#111111]">
            <SEO {...seoByPath["/arkitekter"]} path="/arkitekter" />
            {/* Hero */}
            <section className="pt-40 md:pt-56 pb-20 md:pb-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-4">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                            {t.arkitekter.eyebrow}
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.1} className="col-span-12 md:col-span-8">
                        <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight whitespace-pre-line">
                            {t.arkitekter.title}
                        </h1>
                        <p className="mt-10 max-w-xl text-base md:text-lg text-[#555] leading-relaxed md:leading-relaxed">
                            {t.arkitekter.intro}
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Hero image */}
            <section className="pb-24 md:pb-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <FadeIn>
                        <div className="aspect-[3/2] overflow-hidden bg-[#EBE8E3]">
                            <Img
                                priority
                                src={heroImages.copperDetail}
                                alt="Materials detail"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* Philosophy */}
            <section className="bg-[#F9F8F6] py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-3">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                            {t.arkitekter.philosophyLabel}
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.1} className="col-span-12 md:col-span-9">
                        <p className="font-serif font-light text-3xl md:text-5xl leading-[1.15] tracking-tight">
                            {t.arkitekter.philosophy}
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Capabilities */}
            <section className="py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <FadeIn className="mb-16 md:mb-24">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                            — Capabilities
                        </div>
                        <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1] tracking-tight">
                            {t.arkitekter.capTitle}
                        </h2>
                    </FadeIn>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-20">
                        {caps.map((c, i) => (
                            <FadeIn key={i} delay={(i % 2) * 0.1}>
                                <div className="flex items-baseline gap-6 border-t border-[#D9D4CD] pt-8">
                                    <span className="editorial-number text-lg not-italic">
                                        0{i + 1}
                                    </span>
                                    <div className="flex-1">
                                        <h3 className="font-serif text-3xl md:text-4xl mb-4 font-light">
                                            {c.title}
                                        </h3>
                                        <p className="text-sm md:text-base text-[#555] leading-relaxed md:leading-relaxed max-w-md">
                                            {c.text}
                                        </p>
                                    </div>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA split */}
            <section className="bg-[#0F0F0E] text-white py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-6">
                        <h2 className="font-serif font-light text-4xl md:text-7xl leading-[1] tracking-tight">
                            {t.arkitekter.cta}
                        </h2>
                    </FadeIn>
                    <FadeIn delay={0.1} className="col-span-12 md:col-span-5 md:col-start-8 self-end">
                        <p className="text-sm md:text-base text-white/70 mb-8 leading-relaxed md:leading-relaxed">
                            {t.arkitekter.ctaText}
                        </p>
                        <Link
                            to="/kontakt"
                            data-testid="arkitekter-cta"
                            className="inline-flex items-center gap-3 px-8 py-4 border border-white/70 hover:bg-white hover:text-[#0F0F0E] transition-colors text-[11px] tracking-luxury uppercase"
                        >
                            {t.arkitekter.cta} <ArrowRight size={14} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
};

export default Arkitekter;
